/**
 * headlessExtensions — TipTap extension set for server-side / headless HTML generation.
 *
 * Used by `yDocToHtml()` and `generateHTML()` when the Editor is NOT running.
 * Must match the editor's extension set closely enough that `generateHTML()`
 * can render all node/mark types correctly.
 *
 * Key differences from `createExtensions()`:
 * - No Collaboration extension (no Y.Doc binding needed for HTML generation)
 * - No Placeholder, Selection, UiState (editor-only UI concerns)
 * - No ImageUploadNode (upload widget — never in final doc)
 * - No DragHandle (editor UI concern)
 * - No CollaborationCaret (cursor UI)
 *
 * @see createExtensions.ts for the full editor extension set
 */

import { Node, type AnyExtension } from "@tiptap/core"

/**
 * Stub node for legacy `aiWriter` blocks left in Y.Docs by a previously
 * used TipTap Pro AI extension. Without this, `generateHTML()` throws
 * "Unknown node type: aiWriter".
 */
const AiWriterStub = Node.create({
  name: "aiWriter",
  group: "block",
  content: "block*",
  renderHTML() {
    return ["div", { "data-type": "ai-writer" }, 0]
  },
})

// --- TipTap Core Extensions ---
import { StarterKit } from "@tiptap/starter-kit"
import { TaskList, TaskItem } from "@tiptap/extension-list"
import { Highlight } from "@tiptap/extension-highlight"
import { Typography } from "@tiptap/extension-typography"
import { TextAlign } from "@tiptap/extension-text-align"
import { Mention } from "@tiptap/extension-mention"
import { Emoji, gitHubEmojis } from "@tiptap/extension-emoji"
import { Color, TextStyle } from "@tiptap/extension-text-style"
import { Superscript } from "@tiptap/extension-superscript"
import { Subscript } from "@tiptap/extension-subscript"
import { Mathematics } from "@tiptap/extension-mathematics"

// --- Custom Extensions (node rendering) ---
import { HorizontalRule } from "@/components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension"
import { Image } from "@/components/tiptap-node/image-node/image-node-extension"
import { NodeBackground } from "@/components/tiptap-extension/node-background-extension"
import { NodeAlignment } from "@/components/tiptap-extension/node-alignment-extension"

// --- Table Node ---
import { TableKit } from "@/components/tiptap-node/table-node/extensions/table-node-extension"

/**
 * Headless extensions for `generateHTML()`.
 *
 * Singleton — created once, reused for all conversions.
 * Thread-safe since `generateHTML()` is a pure function (no mutation).
 */
export const headlessExtensions: AnyExtension[] = [
  // ── Core kit ──
  StarterKit.configure({
    undoRedo: false,
    horizontalRule: false,
    dropcursor: false,
    gapcursor: false,
    link: { openOnClick: false },
  }),
  HorizontalRule,

  // ── Text formatting ──
  TextAlign.configure({ types: ["heading", "paragraph"] }),
  TextStyle,
  Color,
  Highlight.configure({ multicolor: true }),
  Superscript,
  Subscript,
  Mathematics,

  // ── Block structure ──
  TaskList,
  TaskItem.configure({ nested: true }),

  // ── Tables ──
  TableKit.configure({
    table: { resizable: false, cellMinWidth: 120 },
  }),

  // ── Node styling ──
  NodeBackground.configure({
    types: [
      "paragraph",
      "heading",
      "blockquote",
      "taskList",
      "bulletList",
      "orderedList",
      "tableCell",
      "tableHeader",
    ],
  }),
  NodeAlignment,

  // ── Inline features ──
  Mention,
  Emoji.configure({
    emojis: gitHubEmojis.filter((emoji) => !emoji.name.includes("regional")),
    forceFallbackImages: true,
  }),

  // ── Media ──
  Image,

  // ── Legacy stubs ──
  AiWriterStub,

  // ── Utility ──
  Typography,
]
